import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Smile, TrendingUp, Check } from "lucide-react";

const pillars = [
  {
    icon: <Sparkles size={24} />,
    overline: "Confidence first — always",
    title: "Helping children feel capable, not pressured",
    body: "Children don't just learn to swim — they build confidence in the water and in themselves. At Happy Tiddlers, confidence comes before technique. Every child progresses at their own pace, in an environment where they feel safe, encouraged and proud.",
    points: [
      "Small class sizes for individual attention",
      "Positive reinforcement and gentle guidance",
      "No pressure, no comparisons — just steady progress",
    ],
    tagline: "From nervous to confident in just a few lessons.",
    img: `${process.env.PUBLIC_URL}/pillar-confidence-pink.jpg`,
    accent: "#FF7A1A",
  },
  {
    icon: <ShieldCheck size={24} />,
    overline: "Safety you can trust",
    title: "Because your child's safety is non-negotiable",
    body: "Families need total trust when it comes to swimming — and safety is at the heart of everything we do. Our lessons are led by highly trained, experienced instructors and delivered in a structured, carefully managed environment designed specifically for children.",
    points: [
      "Qualified, experienced teachers",
      "Clear safety procedures in every lesson",
      "Calm, controlled class structure",
    ],
    tagline: "You can relax knowing your child is learning in safe hands.",
    img: `${process.env.PUBLIC_URL}/pillar-safety.jpg`,
    accent: "#1F7A8C",
  },
  {
    icon: <Smile size={24} />,
    overline: "Joyful learning that works",
    title: "Happy children learn faster",
    body: "Children learn best when they're enjoying themselves. That's why our lessons are playful, engaging and full of positive encouragement. Through games, movement and gentle challenges, children build skills naturally — without fear or pressure.",
    points: [
      "Play-based learning",
      "Encouraging, supportive teaching",
      "Smiling, engaged children in every class",
    ],
    tagline: "Positive first experiences matter — and they last.",
    img: `${process.env.PUBLIC_URL}/pillar-joyful.jpg`,
    accent: "#F59E0B",
  },
  {
    icon: <TrendingUp size={24} />,
    overline: "A skill for life",
    title: "More than lessons — a lifelong foundation",
    body: "Swimming isn't just about learning strokes. It's a life skill that supports safety, confidence and wellbeing long after lessons end. We follow a clear progression pathway so children develop strong foundations and long-term capability in the water — with progress you can see and trust.",
    points: [
      "Structured development stages",
      "Clear milestones and progression",
      "Confident swimmers who grow with us",
    ],
    tagline: "Progress you can see, trust and celebrate.",
    img: `${process.env.PUBLIC_URL}/pillar-skill.jpg`,
    accent: "#6ED3C3",
  },
];

export default function Pillars() {
  return (
    <section id="values" className="py-24 md:py-32 bg-white" data-testid="pillars-section">
      <div className="container mx-auto px-6 md:px-12 space-y-24 md:space-y-32">
        {pillars.map((p, i) => {
          const reverse = i % 2 === 1;
          return (
            <div
              key={p.overline}
              className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
              data-testid={`pillar-${i}`}
            >
              <motion.div
                initial={{ opacity: 0, x: reverse ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                className={reverse ? "lg:order-2" : ""}
              >
                <div className="rounded-[2.5rem] overflow-hidden shadow-xl">
                  <img src={p.img} alt={p.overline} className="w-full h-[380px] md:h-[480px] object-cover" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className={reverse ? "lg:order-1" : ""}
              >
                <span
                  className="inline-flex items-center gap-2 text-sm tracking-[0.2em] uppercase font-bold"
                  style={{ color: p.accent }}
                >
                  <span className="grid place-items-center w-9 h-9 rounded-xl text-white" style={{ backgroundColor: p.accent }}>
                    {p.icon}
                  </span>
                  {p.overline}
                </span>
                <h2 className="mt-5 font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] leading-tight">
                  {p.title}
                </h2>
                <p className="mt-5 text-lg text-[#334155] leading-relaxed">{p.body}</p>
                <ul className="mt-7 space-y-3">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-3">
                      <span className="grid place-items-center w-6 h-6 mt-0.5 rounded-full text-white shrink-0" style={{ backgroundColor: p.accent }}>
                        <Check size={14} strokeWidth={3} />
                      </span>
                      <span className="text-[#334155] text-lg">{pt}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-7 font-heading text-xl font-semibold text-[#0A2540]">{p.tagline}</p>
              </motion.div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
